import detectEthereumProvider from '@metamask/detect-provider'
import { Api } from 'eosjs'
import { Serializer, Action } from '@greymass/eosio'
import Web3 from 'web3'
import type { EosAccount, EosAction, EosConfig, TransactionResult, WalletAdapter } from '@/types'

declare global {
  interface Window {
    ethereum?: any
  }
}

export class MetamaskWallet implements WalletAdapter {
  private provider: any = null
  private web3: any = null
  private chainId = 59
  private currentAccount: EosAccount | null = null
  private rpc: any = null
  private api: any = null
  private rp = 0

  async connect(_config: EosConfig, rpc: unknown): Promise<EosAccount[]> {
    this.rpc = rpc
    this.api = new Api({
      rpc: rpc as any,
      signatureProvider: { getAvailableKeys: async () => [], sign: async (args: any) => args } as any,
      textDecoder: new TextDecoder(),
      textEncoder: new TextEncoder(),
    })
    const hasProvider = await this.isProvider()
    if (hasProvider) {
      window.ethereum?.request({
        method: 'wallet_addEthereumChain',
        params: [{
          chainId: '0x3B',
          chainName: 'EOS Chain',
          nativeCurrency: { name: 'EOS', symbol: 'EOS', decimals: 18 },
          rpcUrls: ['https://api.metahub.cash'],
          blockExplorerUrls: ['https://bloks.io/'],
        }],
      })
      await this.waitConnect()
      window.ethereum.on('accountsChanged', this.handleAccountsChanged.bind(this))
      const accounts = await window.ethereum.request({ method: 'eth_accounts' })
      if (accounts?.length) {
        this.currentAccount = await this.accountInfo(accounts[0])
        return [this.currentAccount]
      }
    }
    return []
  }

  private async waitConnect(): Promise<boolean> {
    const isConnect = window.ethereum.isConnected()
    if (!isConnect) {
      await new Promise((r) => setTimeout(r, 50))
      return this.waitConnect()
    }
    return true
  }

  private async isProvider(): Promise<boolean> {
    this.provider = await detectEthereumProvider()
    if (this.provider) {
      this.web3 = new Web3(window.ethereum)
      return true
    }
    return false
  }

  private async handleAccountsChanged(accounts: string[]): Promise<void> {
    if (accounts.length === 0) {
      console.log('Please connect to MetaMask.')
    } else if (this.currentAccount && accounts[0] !== this.currentAccount.address) {
      this.currentAccount = await this.accountInfo(accounts[0])
    }
  }

  async login(): Promise<EosAccount[]> {
    const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' })
    if (accounts?.length) {
      this.currentAccount = await this.accountInfo(accounts[0])
      return [this.currentAccount]
    }
    return []
  }

  async logout(): Promise<void> {
    this.currentAccount = null
  }

  async transact(actions: EosAction[]): Promise<TransactionResult> {
    const serializedActions = await this.api.serializeActions(actions)
    const actionBytes = Serializer.encode({
      object: serializedActions,
      type: 'action[]',
      customTypes: [Action],
    } as any).array

    const abi = [
      {
        inputs: [
          { internalType: 'uint64', name: 'rp', type: 'uint64' },
          { internalType: 'bytes', name: 'actions', type: 'bytes' },
        ],
        name: 'pushEosTransaction',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
      },
      {
        inputs: [],
        name: 'getRp',
        outputs: [{ internalType: 'uint64', name: '', type: 'uint64' }],
        stateMutability: 'view',
        type: 'function',
      },
    ]

    const address = '0xa1050456bf9f78d485445fb43aa2c6978f3aa5d5'
    const ethAccContract = new this.web3.eth.Contract(abi, address)
    if (this.rp === 0) {
      this.rp = await ethAccContract.methods.getRp().call()
    }
    const from = this.currentAccount!.address!
    const gasPrice = await this.web3.eth.getGasPrice()
    const nonce = await this.web3.eth.getTransactionCount(from)
    const actionHex = this.web3.utils.bytesToHex(actionBytes)
    const data = ethAccContract.methods.pushEosTransaction(this.rp, actionHex).encodeABI()

    const input: any = {
      from,
      to: address,
      gasPrice: this.web3.utils.toHex(gasPrice),
      nonce: this.web3.utils.toHex(nonce),
      value: '0x0',
      data,
    }
    const gasLimit = await this.web3.eth.estimateGas(input)
    input.gasLimit = this.web3.utils.toHex(gasLimit)
    input.chainId = this.web3.utils.toHex(this.chainId)

    const trxid = await new Promise<string>((resolve, reject) => {
      this.web3.eth
        .sendTransaction(input)
        .once('transactionHash', (hash: string) => resolve(hash))
        .on('error', (error: Error, receipt: unknown) => {
          reject({ code: 'receipt', receipt, error })
        })
    })

    return { transaction_id: trxid, processed: {} }
  }

  private async accountInfo(account: string): Promise<EosAccount> {
    const eosAccount = await this.getEOSAccount(account)
    const shortName = account.substring(0, 4) + '..' + account.slice(-6)
    return {
      shortName,
      name: eosAccount,
      address: account,
      authority: 'active',
    }
  }

  private async getEOSAccount(account: string): Promise<string> {
    const params = {
      code: 'etheraccount',
      scope: 'etheraccount',
      table: 'account',
      index_position: 'secondary',
      key_type: 'sha256',
      lower_bound: account.substring(2),
      upper_bound: account.substring(2),
      limit: 1,
    }
    try {
      const result = await this.rpc.get_table_rows(params)
      if (result?.rows?.length) {
        return result.rows[0].eos_account
      }
    } catch {
      console.log('not found account')
    }
    return 'null.ttr'
  }
}
